import type { OrderState } from '@/domain/Order';
import { api } from '@/lib/api';
import { mutationOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';
import type { OrderResponse } from './orderTypes';

export interface UpdateOrderStateRequest {
  state: OrderState;
}

export const updateOrderStateMutation = () =>
  mutationOptions({
    mutationKey: mutationKeys.orders.updateState(),
    mutationFn: async ({
      orderId,
      state,
      send_all,
    }: {
      orderId: number;
      state: OrderState;
      send_all?: boolean;
    }) => {
      return await api
        .patch(`orders/${orderId}/state`, { json: { state, send_all } })
        .json<OrderResponse>();
    },
    onSuccess: () => {
      toast.success('Order state updated successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update order state');
    },
  });

export interface SetOrderParcelRequest {
  /** Tracking number — the only field needed to submit a manual parcel. */
  parcel_track: string;
}

/** PATCH orders/{order}/parcel — manually set parcel info (no auto-create). */
export const setOrderParcelMutation = () =>
  mutationOptions({
    mutationKey: mutationKeys.orders.setParcel(),
    mutationFn: async ({
      orderId,
      ...body
    }: SetOrderParcelRequest & { orderId: number }) => {
      return await api
        .patch(`orders/${orderId}/parcel`, { json: body })
        .json<OrderResponse>();
    },
    onSuccess: () => {
      toast.success('Parcel info saved');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save parcel info');
    },
  });

export const updateOrderNoteMutation = () =>
  mutationOptions({
    mutationKey: mutationKeys.orders.updateNote(),
    mutationFn: async ({
      orderId,
      note,
    }: {
      orderId: number;
      note: string;
    }) => {
      return await api
        .patch(`orders/${orderId}/note`, { json: { note } })
        .json<OrderResponse>();
    },
    onSuccess: () => {
      toast.success('Note saved');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save note');
    },
  });
